import React, { useState, useCallback, useEffect, ReactElement, useRef } from 'react';
import styled from 'styled-components';
import {
  TypeTable,
  MultilineContent,
  useNotification,
  Icon,
  FilterBar,
  SortDropdown,
  IFilterDatePicker,
  IFilterItem,
  IFilterValue,
  DateInterval,
  IFilterDropdownConfig,
  IFilterResult
} from 'scorer-ui-kit';
import i18n from 'i18next';

import { useLocation, useParams } from 'react-router-dom';
import { ITableColumnConfig, ITypeTableData, IRowData } from 'scorer-ui-kit/dist/Tables';
import { formatDate, formatDateToJST } from '../util';
import Pagination from '../components/Pagination';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { getSourceFiles, Movie, PipeLineExecution, pipelineJobs, startCSVDownload } from '../lib/api_config';

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const TimeText = styled.div`
font-size: 12px;
span {
  font-style: italic;
}
`;

const ActionColumnContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const IconWrapper = styled.div`
  cursor: pointer;
`;

const FilterButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 43px;
`;

const SortingWrapper = styled.div`
  height: 128px;
  padding-top: 31px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  position: 'relative';
`;

const FilterResultsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 60px;
`;

interface Params {
  name: string,
  lid: string
}

const generateTimeRows = (initTime: string, endTime: string) : ReactElement[] =>  {
  return (
    [
      <TimeText key='top'>{`${initTime} ${String.fromCharCode(160)} →`}</TimeText>,
      <TimeText key='bottom'>{endTime}<span>{' JST'}</span></TimeText>
    ]
  );
};

interface JobMoviesMap {
  [jobID: string]: Movie;
}

interface IInterval {
  start?: Date | null | string,
  end?: Date | null | string
}

interface IAppFilter {
  startDateAJ: string;
  endDateAJ: string;
  status: string;
  pageSize: string;
  sortByAJ: string;
  paramsLoaded: string;
  datePickerForRuntime?: IInterval;
}
  

const defaultFilterState: IAppFilter = {
  startDateAJ: '',
  endDateAJ: '',
  status: '',
  sortByAJ: '',
  pageSize: '',
  paramsLoaded: '',
  datePickerForRuntime: {start: new Date(), end: new Date()},
};

interface INewParams {
  sortByAJ?: string | null;
  pageSize?: string | null;
  startDateAJ?: string | null;
  endDateAJ?: string | null;
  status?: string | null;
  selectedTab?:string | null;
}

interface IFilterData {
  paramsLoaded: string;
  pageSize: string;
  sortByAJ:string;
  startDateAJ: string;
  endDateAJ: string;
  datePickerForRuntime: IInterval;
  status: string | null;
}

interface ICameraDetailsJobs {
  getStatus:(status:string) => void;
  getAPSorting: (sort:any) => void;
  getAJDates:(data: any) => void;
  ajDates: any;
  statusParent: string | null;
}

const FILTER_DATA: IFilterData = {status: '', pageSize: '', sortByAJ: '', paramsLoaded: '', endDateAJ: '', startDateAJ: '', datePickerForRuntime: {} };
const getSelectedFilter = (text: string, value: DateInterval | undefined | string | Date) => (value === '' ? undefined : ({ text, value }));

const AnalysisJobsTab: React.FC<ICameraDetailsJobs> = ({getStatus, getAPSorting, ajDates, getAJDates, statusParent}) => {
  const [rowData, setRowData] = useState<ITypeTableData>([]);
  const {lid}: Params = useParams();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const {t} = useTranslation(['CameraDetails', 'Common']);
  const [jobs, setJobs] = useState<PipeLineExecution[]>([]);
  const [jobMoviesMap, setJobMoviesMap] = useState<JobMoviesMap>({});
  const [loading, setLoading] = useState(true);
  const {sendNotification} = useNotification();
  const [sortType, setSortType] = useState('descending');
  const dateRef = useRef<HTMLDivElement>(null);
  const [filterValues, updateFilter] = useState<IAppFilter>(defaultFilterState);
  const [sorting, setSorting] = useState({ value: 'recordingTime', ascending: false, text: t('Recording Time') });
  const params = useLocation().search;
  const [historyParams] = useState(params);

  const fetchHistoryParams = useCallback(() => {
    const params = new URLSearchParams(historyParams);
    const newParams: INewParams = {};
    newParams.sortByAJ = params.get('sortByAJ');
    newParams.pageSize = params.get('pageSize');
    newParams.status = params.get('status');
    newParams.startDateAJ = params.get('startDateAJ');
    newParams.endDateAJ = params.get('endDateAJ');

    const searchFilter: any = { ...FILTER_DATA };
    searchFilter.status = newParams.status ?? '';
    setSorting(prev => (newParams.sortByAJ ? JSON.parse(newParams.sortByAJ) : prev));
    if(newParams.sortByAJ) {
      const sortValue = Object.values(JSON.parse(newParams.sortByAJ));
      if(sortValue) {
        setSortType('ascending');
      } else {
        setSortType('descending');
      }
    } else {
      setSortType('descending');
    }
    if(newParams.pageSize){
      setPageSize(prev => (newParams.pageSize ? JSON.parse(newParams.pageSize) : prev));
    }
    if(ajDates) {
      if(ajDates.startDateAJ !== 'null') {
        searchFilter.startDateAJ = format(new Date(ajDates.startDateAJ), 'yyyy-MM-dd HH:mm:ss');
        searchFilter.endDateAJ = format(new Date(ajDates.endDateAJ), 'yyyy-MM-dd HH:mm:ss');
      }
    }
    if(statusParent !== '') {
      searchFilter.status = statusParent;
    }
    searchFilter.paramsLoaded = 'true';
    updateFilter(searchFilter);
  }, [historyParams, ajDates, statusParent]);

  useEffect(() => {
    fetchHistoryParams();
  }, [fetchHistoryParams]);

  const sortByList = [
    {
      value: 'recordingTime',
      text: t('analysisJobsTab.jobCreatedAt')
    }
  ];

  const dropdownConfig: IFilterDropdownConfig[] = [
    {
      id: 'status',
      buttonText: t('analysisJobsTab.status'),
      buttonIcon: 'Camera',
      optionType: 'radio',
      list: [{text:t('analysisJobsTab.Failed'), value: 'failed'}, {text: t('analysisJobsTab.Completed'), value: 'completed'}, {text: t('analysisJobsTab.Submitted'), value: 'submitted'}] as IFilterItem[],
      selected: getSelectedFilter(t(filterValues.status), filterValues.status) as IFilterValue | undefined
    },
  ];

  const dateConfig:IFilterDatePicker[] = [
    {
      buttonIcon: 'DateTime',
      buttonText: t('analysisJobsTab.jobCreatedAt'),
      dateMode: 'interval',
      id: 'datePickerForRuntime',
      timeMode: 'interval',
      lang: i18n.language === 'ja' ? 'ja' : 'en',
      timeZoneTitle: t('Common:timeZoneTitle'),
      dateTimeTextUpper: t('Common:from'),
      dateTimeTextLower: t('Common:to'),
      selected:filterValues.startDateAJ !== '' ? {start: new Date(filterValues.startDateAJ), end: new Date(filterValues.endDateAJ)} : null
    }
  ];

  const startJobDownload = useCallback(async ({jobID}: {jobID: string|null}) => {
    if(!jobID) return;
    try {
      sendNotification({
        message : t('preparingDownload'),
        type: 'info'
      });
      const {url } = await startCSVDownload({deviceID: lid, jobID});
      console.log(url);
      downloadLink(url);

    } catch(error: any){
      console.error (t('errorWhileStartingDownload'), error.message);

    }

  },[lid, sendNotification, t]);

  const getJobMovies = useCallback(async(jobs: string[])=>{
    setLoading(true);
    const jobMoviesMap = {} as JobMoviesMap;
    const jobMoviePromises = jobs.map( async (jobID) => {
      const {movieList: [movie]=[]} = await getSourceFiles({deviceID:lid,jobID, pageSize: 1});
      jobMoviesMap[jobID] = movie;
    });
    try{
      await Promise.all(jobMoviePromises);
    }catch(error){
      console.log(error);
    }
    setJobMoviesMap(jobMoviesMap);
    setLoading(false);
  },[lid]);

  const getJobs = useCallback(async()=>{
    if(filterValues.paramsLoaded === 'true') {
      const startDateAJ = filterValues.startDateAJ ? format(utcToZonedTime(new Date(filterValues.startDateAJ), 'UTC'), 'yyyy-MM-dd HH:mm:ss') : '';
      const endDateAJ = filterValues.endDateAJ ? format(utcToZonedTime(new Date(filterValues.endDateAJ), 'UTC'), 'yyyy-MM-dd HH:mm:ss') : '';
      const status = filterValues.status === '' ? '' : filterValues.status;
      const jobs = await pipelineJobs(lid, status, startDateAJ, endDateAJ, sortType);
      setJobs(jobs);
      getJobMovies(jobs.reduce((jobIDs: string[], {stages:[{jobID=null}={}]=[]}) => jobID ? [jobID,...jobIDs]:jobIDs, []) );
    }
    
  },[getJobMovies, lid, filterValues, sortType]);

  const sizeOptions = [10, 20, 50, 100];
  const JobsTableColumns: ITableColumnConfig[] = [
    {
      header: t('analysisJobsTab.recordingDateRange'),
      sortable: false,
      cellStyle: 'normalImportance',
      minWidth: 210
    },
    {
      header: t('analysisJobsTab.jobCreatedAt'),
      sortable: false,
      cellStyle: 'normalImportance',
      minWidth: 110
    },
    {
      header: t('analysisJobsTab.status'),
      sortable: false,
      cellStyle: 'normalImportance',
      alignment: 'center',
      minWidth: 110
    },
    {
      header: t('actions'),
      sortable: false,
      cellStyle: 'normalImportance',
      alignment: 'center',
      minWidth: 40
    }
  ];

  const generateActionColumn = useCallback((movieLink: string, aggregationJobID: string | null): ReactElement[] => {
    return (
      [
        <ActionColumnContainer key=''>
          <IconWrapper title={t('analysisJobsTab.downloadAnalyzedVideo')} onClick={() => downloadLink(movieLink)}>
            <Icon icon='DownloadVideo' color='dimmed' size={20} />
          </IconWrapper>
          <IconWrapper style={{'marginLeft': '15px'}} title={t('analysisJobsTab.downloadAnalysisLog')} onClick={() => startJobDownload({jobID: aggregationJobID})}>
            <Icon icon='Download' color='dimmed' size={20} />
          </IconWrapper>
        </ActionColumnContainer>
      ]
    );
  }, [t, startJobDownload]);

  const generateRowData = useCallback((): ITypeTableData => {
    const data: ITypeTableData = [];
    jobs.forEach(({status,analysisStartDate,analysisEndDate,createdDate, stages}) => {
      const [{jobID:videoJobID=null}={},{jobID: aggregationJobID=null}={}] = stages;
      const {SignedImageUrl: movieThumbnail,SignedMovieUrl: movieLink} = (videoJobID && jobMoviesMap?.[videoJobID])||{} as Movie;

      const row: IRowData = {
        header: {
          icon: 'Camera',
          image: movieThumbnail,
          mediaType: 'video',
          mediaUrl: movieLink
        },
        columns: [
          { customComponent: <MultilineContent contentArray={generateTimeRows(formatDate(analysisStartDate),formatDate(analysisEndDate))} />},
          { text: formatDateToJST(createdDate, 'yyyy/MM/dd - HH:mm') },
          { text: t(`analysisJobsTab.${status.charAt(0).toUpperCase() + status.slice(1)}`) },
          { customComponent: <MultilineContent contentArray={generateActionColumn(movieLink, aggregationJobID)} />},
        ]
      };
      data.push(row);
    });

    return data;

  }, [jobMoviesMap, jobs, t, generateActionColumn]);


  useEffect(()=>{
    getJobs();
  },[getJobs]);

  useEffect(() => {
    setRowData(generateRowData);
  }, [generateRowData]);

  const onSizePerPageChange = useCallback((size: number) => {
    setPageSize(size);
  }, [setPageSize]);

  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
  },[setCurrentPage]);

  const onChangeSorting1 = useCallback((event, ascending) => {
    setSorting({ value: event.value.toString(), text: event.text, ascending });
    getAPSorting({ value: event.value.toString(), text: event.text, ascending });
    if(ascending){
      setSortType('ascending');
    } else {
      setSortType('descending');
    }
  }, [getAPSorting]);

  const handleFilter = useCallback((res: IFilterResult[]) => { 
    const searchFilter:any = {...defaultFilterState};
    res.forEach((item) => {
      if(item.id === 'datePickerForRuntime') {
        searchFilter[item.id] = item.selected as DateInterval;
        searchFilter.startDateAJ = searchFilter[item.id]?.start?.toString() || '';
        searchFilter.endDateAJ = searchFilter[item.id]?.end?.toString() || '';
        getAJDates({startDateAJ: format(new Date(searchFilter.startDateAJ), 'yyyy-MM-dd HH:mm:ss'), endDateAJ: format(new Date(searchFilter.endDateAJ), 'yyyy-MM-dd HH:mm:ss')});
      } else {
        getStatus((item.selected as IFilterItem).value as string);
        searchFilter[item.id] = (item.selected as IFilterItem).value as string;
      }
    });
    if(searchFilter.startDateAJ === '') {
      getAJDates(null);
    }
    if(searchFilter.status === '') {
      getStatus('');
    }
    searchFilter.paramsLoaded = 'true';
    updateFilter(searchFilter);
    setCurrentPage(1);
  },[getStatus, getAJDates]);

  return (
    <>
      <FilterButtonContainer>
        {filterValues.paramsLoaded === 'true' &&
          <FilterContainer ref={dateRef}>
            <FilterBar filtersTitle={t('Common:filters')+':'} resultTextTemplate={t('Common:showingResults') + t(' ({X})').replace('{X}', '' + rowData.length) + ':'} totalResults={rowData.length} resultsDateFormat='yyyy/MM/dd - HH:mm' clearText={t('Common:clearAll')} datePickersConfig={dateConfig} dropdownsConfig={dropdownConfig} onChangeCallback={handleFilter} />
          </FilterContainer>}
        <SortingWrapper>
          <SortDropdown
            ascendingText={t('Common:sorting.ascending')}
            buttonText={t('Common:sortedBy') + ' ' + t('analysisJobsTab.jobCreatedAt')}
            descendingText={t('Common:sorting.descending')}
            isSortAscending={sorting.ascending}
            list={sortByList}
            onSelect={onChangeSorting1}
            selected={{ text: sorting.text, value: sorting.value }}
          />
        </SortingWrapper>
      </FilterButtonContainer>
      <FilterResultsContainer />
      <TypeTable
        columnConfig={JobsTableColumns}
        rows={rowData.length > 0 ? rowData : [{ columns: [] }]}
        isLoading={loading}
        hasTypeIcon
        hasThumbnail
        loadingText=''
        emptyTableText={t('Common:noDataFound')}
      />
      <PaginationContainer>
        <Pagination
          pageSizeOptions={sizeOptions}
          totalPages={5}
          defaultPage={currentPage}
          defaultPageSize={pageSize}
          onPageSizeChange={onSizePerPageChange}
          onPageChange={onPageChange}
          pageSizeText={t('Common:pagination.itemsPerPage')}
          prevPageText='<'
          nextPageText='>'
          firstPageText='<<'
          lastPageText='>>'
        />
      </PaginationContainer>
    </>
  );
};

export default AnalysisJobsTab;

const downloadLink = (link: string) => {
  if(!link){return;}
  const anchor = document.createElement('a');
  anchor.href = link;
  // link.download = "name_of_file_with_extension";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};